import { Injectable } from '@angular/core';
import {
  ConfigurationService,
  FeatureService,
  GlobalStore,
} from '@maximizer/core/shared/data-access';
import {
  ConfigurationSetting,
  ConfigurationSettingCodes,
} from '@maximizer/core/shared/domain';
import {
  CODE_ADMIN,
  createDefaultIqBoostUserSavedConfiguration,
  DefaultDataScope,
  GenerateIqBoostRequest,
  IQBoostSavedConfigurationUser,
  IqBoostPromptCategory,
  MaximizerAiService,
  PIIContext,
  parseIqBoostConfigurationRecord,
  SaveQueue,
} from '@maximizer/ai/data-access';
import {
  EntryStore,
  OutlookService,
  OutlookStore,
} from '@maximizer/outlook/shared/data-access';
import {
  Observable,
  catchError,
  forkJoin,
  from,
  map,
  of,
  reduce,
  switchMap,
  tap,
} from 'rxjs';
import {
  EMAIL_DRAFT_AUTHOR_FALLBACK_INSTRUCTION,
  OutlookAbEntryDetails,
} from '@maximizer/outlook/shared/domain';
import { OutlookIqBoostStore } from '../state/iq-boost.store';
import { IqPromptSelection } from '../models/iq-boost';
import { OutlookAiApiService } from './ai-api.service';
import {
  DATA_PROCESSING_SETTING_CODES,
  isAdminAnonymizationEnabled,
  parseDataProcessingRecord,
  resolveAnonymizationEnabled,
} from '../utils/data-processing-config';
import {
  EMAIL_ASSIST_TAG,
  EMAIL_DRAFT_SYSTEM_PROMPT_NAME,
  buildCurrentUserContextMessage,
  buildEmailSubjectContextMessage,
  buildEmailToReplyToMessage,
  buildReplyRecipientContextMessage,
  sortPromptsByDisplayOrder,
  toIqPromptSelection,
  toPIIContactRecords,
  toParticipantPIIContactRecord,
} from '../utils/email-assist';

const MAX_RECENT_PROMPTS = 5;
const OUTLOOK_PROMPT_PROJECT = 'Outlook';

const OUTLOOK_IQ_BOOST_SETTING_CODES: ConfigurationSettingCodes = {
  code3: 'OutlookIqBoost',
  code4: 'Settings',
};

@Injectable({ providedIn: 'root' })
export class OutlookRecentPromptSaveQueue extends SaveQueue {}

@Injectable()
export class OutlookIqBoostService {
  private iqBoostSetting: ConfigurationSetting | null = null;

  constructor(
    private readonly maximizerAi: MaximizerAiService,
    private readonly globalStore: GlobalStore,
    private readonly entryStore: EntryStore,
    private readonly outlookStore: OutlookStore,
    private readonly iqBoostStore: OutlookIqBoostStore,
    private readonly configurationService: ConfigurationService,
    private readonly aiApi: OutlookAiApiService,
    private readonly feature: FeatureService,
    private readonly outlookService: OutlookService,
    private readonly recentPromptSaves: OutlookRecentPromptSaveQueue,
  ) {}

  loadPromptCatalog(): Observable<IqBoostPromptCategory[]> {
    return this.maximizerAi
      .getPrompts({ language: this.outlookStore.language(), type: 'single' })
      .pipe(
        tap((promptCategories) =>
          this.iqBoostStore.setPromptCategories(promptCategories),
        ),
        catchError(() => {
          this.iqBoostStore.setPromptCategories([]);
          return of([] as IqBoostPromptCategory[]);
        }),
      );
  }

  loadEmailAssistPrompts(): Observable<IqPromptSelection[]> {
    return this.maximizerAi
      .lookupPrompts({
        project: OUTLOOK_PROMPT_PROJECT,
        filter: { tag: [EMAIL_ASSIST_TAG] },
      })
      .pipe(
        map((lookupResults) =>
          sortPromptsByDisplayOrder(
            lookupResults.map((lookupResult) =>
              toIqPromptSelection(lookupResult, this.outlookStore.language()),
            ),
          ),
        ),
        tap((prompts) => this.iqBoostStore.setEmailAssistPrompts(prompts)),
        catchError(() => {
          this.iqBoostStore.setEmailAssistPrompts([]);
          return of([] as IqPromptSelection[]);
        }),
      );
  }

  loadRecentPrompts(): Observable<void> {
    if (!this.globalStore.session()?.user.id) return of(void 0);
    return this.recentPromptSaves
      .enqueue(() => of(true))
      .pipe(
        switchMap(() =>
          this.configurationService.getSetting(this.userSettingCodes()),
        ),
        tap((userSetting) => {
          const parsed = parseIqBoostConfigurationRecord<
            Partial<IQBoostSavedConfigurationUser>
          >(userSetting?.textValue, () => ({}));
          const defaults = createDefaultIqBoostUserSavedConfiguration();
          const userConfig: IQBoostSavedConfigurationUser = {
            ...defaults,
            ...parsed,
            dataProcessingFeatures: {
              ...defaults.dataProcessingFeatures,
              anonymized: this.feature.isFeatureOn(
                'core-ai-anonymizer-enabled',
                false,
              ),
              ...parsed.dataProcessingFeatures,
            },
            dataScope: {
              ...DefaultDataScope,
              ...parsed.dataScope,
            },
          };
          this.iqBoostStore.setRecentPrompts(
            userConfig.recentUserPrompts ?? [],
          );
          this.outlookStore.setUserAnonymizationEnabled(
            resolveAnonymizationEnabled(
              userSetting,
              this.outlookStore.adminDataProcessingFeatures(),
            ),
          );
          this.outlookStore.setUserGlobalServersEnabled(
            userConfig.dataProcessingFeatures?.useGlobalServers ?? false,
          );
          this.outlookStore.setUserWebDataEnabled(
            userConfig.dataProcessingFeatures?.useWebData ?? false,
          );
          this.iqBoostStore.setDataScope(
            userConfig.dataScope ?? DefaultDataScope,
          );
        }),
        map(() => void 0),
      );
  }

  loadAdminPrivacySettings(): Observable<void> {
    return this.configurationService
      .getSetting({ ...DATA_PROCESSING_SETTING_CODES, code2: CODE_ADMIN })
      .pipe(
        tap((adminSetting) => {
          if (adminSetting?.textValue) {
            const config = parseDataProcessingRecord(adminSetting.textValue);
            if (config.dataProcessingFeatures) {
              this.outlookStore.setAdminDataProcessingFeatures(
                config.dataProcessingFeatures,
              );
              if (isAdminAnonymizationEnabled(config.dataProcessingFeatures)) {
                this.outlookStore.setUserAnonymizationEnabled(true);
              }
            }
            if (config.dataScope) {
              this.outlookStore.setAdminDataScope(config.dataScope);
            }
          }
        }),
        map(() => void 0),
      );
  }

  private userSettingCodes(): ConfigurationSettingCodes {
    return {
      ...DATA_PROCESSING_SETTING_CODES,
      code2: this.globalStore.session()?.user.id ?? '',
    };
  }

  loadFirstTimeLaunch(): Observable<boolean> {
    const cached = this.iqBoostStore.firstTimeLaunch();
    if (cached !== null) {
      return of(cached);
    }

    const session = this.globalStore.session();
    if (!session) {
      return of(false);
    }

    const settingCodes = {
      ...OUTLOOK_IQ_BOOST_SETTING_CODES,
      code2: session.user.id,
    };

    return this.configurationService.getSetting(settingCodes).pipe(
      switchMap((setting) => {
        if (setting) {
          this.iqBoostSetting = setting;
          const parsed = setting.textValue
            ? JSON.parse(setting.textValue)
            : { firstTimeLaunch: false };
          const firstTimeLaunch = parsed.firstTimeLaunch === true;
          this.iqBoostStore.setFirstTimeLaunch(firstTimeLaunch);
          return of(firstTimeLaunch);
        }

        this.iqBoostStore.setFirstTimeLaunch(true);
        return this.configurationService
          .updateSetting(
            null,
            settingCodes,
            JSON.stringify({ firstTimeLaunch: false }),
          )
          .pipe(
            tap((newSetting) => {
              if (newSetting) this.iqBoostSetting = newSetting;
            }),
            map(() => true),
          );
      }),
      catchError(() => of(false)),
    );
  }

  pushRecentPrompt(prompt: string): void {
    const trimmed = prompt?.trim();
    if (!trimmed) return;
    if (!this.globalStore.session()?.user.id) return;

    const updated = [
      trimmed,
      ...this.iqBoostStore
        .recentPrompts()
        .filter((existingPrompt) => existingPrompt.trim() !== trimmed),
    ].slice(0, MAX_RECENT_PROMPTS);

    this.iqBoostStore.setRecentPrompts(updated);

    const codes = this.userSettingCodes();
    this.recentPromptSaves
      .enqueue(() =>
        this.configurationService.getSetting(codes).pipe(
          switchMap((setting) => {
            const storedRecord = parseIqBoostConfigurationRecord(
              setting?.textValue,
              createDefaultIqBoostUserSavedConfiguration,
            );
            return this.configurationService.updateSetting(
              setting?.id ?? null,
              codes,
              JSON.stringify({ ...storedRecord, recentUserPrompts: updated }),
            );
          }),
          map((setting) => setting !== null),
        ),
      )
      .subscribe();
  }

  private get isAnonymizationEnabled(): boolean {
    return (
      isAdminAnonymizationEnabled(
        this.outlookStore.adminDataProcessingFeatures(),
      ) || this.outlookStore.userAnonymizationEnabled()
    );
  }

  generateResponse(
    selection: IqPromptSelection,
    invalidateCache = false,
  ): Observable<string> {
    if (selection.isEmailAssist) {
      return this.generateEmailAssistResponse(selection, invalidateCache);
    }

    return this.generateAndAccumulate({
      entityKey: this.entryKey(),
      promptName: selection.name,
      entityType: this.entityType(),
      language: this.outlookStore.language(),
      invalidateCache,
    });
  }

  generateChatResponse(question: string): Observable<string> {
    const promptName =
      this.iqBoostStore.currentPromptSelection()?.name ??
      this.firstCatalogPromptName();
    const entityKey = this.entryKey();

    return this.generateAndAccumulate({
      entityKey,
      promptName,
      entityType: this.entityType(),
      userPrompt: question,
      conversationId: this.iqBoostStore.startConversation(
        entityKey,
        promptName,
      ),
      language: this.outlookStore.language(),
    });
  }

  /** The server may stream (SSE) or buffer; both fold into one final string. */
  private generateAndAccumulate(
    request: GenerateIqBoostRequest,
  ): Observable<string> {
    return this.maximizerAi
      .generateResponse(request)
      .pipe(
        reduce((accumulated, chunk) => accumulated + (chunk.content ?? ''), ''),
      );
  }

  private firstCatalogPromptName(): string {
    return (
      this.iqBoostStore
        .promptCategories()
        .flatMap((category) => category.prompts)[0]?.name ?? ''
    );
  }

  private entryKey(): string {
    return (this.entryStore.entry()?.key as string) ?? '';
  }

  private entityType(): string {
    return this.entryStore.type() === 'lead' ? 'lead' : 'addressBook';
  }

  private async buildEmailParticipantPiiContext(): Promise<PIIContext> {
    const entry = this.entryStore.entry() as OutlookAbEntryDetails | null;
    const entryContacts = entry?.key ? toPIIContactRecords(entry) : [];
    const participantContacts = (
      await this.outlookService.getParticipantEmails()
    ).map(toParticipantPIIContactRecord);
    const currentUser = this.getCurrentUserContact();
    return {
      contacts: [
        ...entryContacts,
        ...participantContacts,
        ...(currentUser ? [currentUser] : []),
      ],
    };
  }

  private getCurrentUserContact() {
    const profile = this.outlookService.mailbox?.userProfile;
    if (!profile?.emailAddress) return null;
    return toParticipantPIIContactRecord({
      displayName: profile.displayName,
      emailAddress: profile.emailAddress,
    } as Office.EmailAddressDetails);
  }

  // Read mode only: in compose mode `item.from` is the composer, not the
  // person being replied to.
  private getReplyRecipientContact() {
    const item = this.outlookService.mailboxItem;
    if (!item || this.outlookService.isCompose) return null;
    const sender = this.outlookService.getSenderEmail(item);
    if (!sender?.displayName && !sender?.emailAddress) return null;
    return toParticipantPIIContactRecord(sender);
  }

  // Email content exists only in Office.js and travels as additionalMessages,
  // which skip server-side anonymization — redaction/anonymization happen here.
  private generateEmailAssistResponse(
    selection: IqPromptSelection,
    invalidateCache: boolean,
  ): Observable<string> {
    return forkJoin({
      bodyText: this.outlookService.getMessageBodyText(),
      subject: from(this.outlookService.getSubject(true)),
      authorInstruction: selection.isDraft
        ? this.loadDraftAuthorInstruction()
        : of(null),
    }).pipe(
      switchMap(({ bodyText, subject, authorInstruction }) =>
        from(this.buildEmailParticipantPiiContext()).pipe(
          switchMap((emailPiiContext) =>
            this.aiApi.anonymizeContextAndUser(
              this.isAnonymizationEnabled,
              this.buildEmailAssistMessages(selection, bodyText, subject),
              [],
              [],
              emailPiiContext,
            ),
          ),
          switchMap(({ context }) =>
            this.generateAndAccumulate({
              entityKey: this.entryKey(),
              promptName: selection.name,
              entityType: this.entityType(),
              language: this.outlookStore.language(),
              invalidateCache,
              additionalMessages: {
                user: context.map((message) => message.content),
                ...(selection.isDraft
                  ? {
                      system: [
                        authorInstruction ??
                          EMAIL_DRAFT_AUTHOR_FALLBACK_INSTRUCTION,
                      ],
                    }
                  : {}),
              },
            }),
          ),
        ),
      ),
    );
  }

  private buildEmailAssistMessages(
    selection: IqPromptSelection,
    bodyText: string | null,
    subject: string | null,
  ): { role: 'user'; content: string }[] {
    const currentUser = selection.isDraft ? this.getCurrentUserContact() : null;
    const replyRecipient = selection.isDraft
      ? this.getReplyRecipientContact()
      : null;

    return [
      ...(subject
        ? [
            {
              role: 'user' as const,
              content: buildEmailSubjectContextMessage(subject),
            },
          ]
        : []),
      ...(replyRecipient
        ? [
            {
              role: 'user' as const,
              content: buildReplyRecipientContextMessage(
                replyRecipient.fullName ?? '',
                replyRecipient.email ?? '',
              ),
            },
          ]
        : []),
      ...(currentUser
        ? [
            {
              role: 'user' as const,
              content: buildCurrentUserContextMessage(
                currentUser.fullName ?? '',
                currentUser.email ?? '',
              ),
            },
          ]
        : []),
      {
        role: 'user' as const,
        content: selection.isDraft
          ? buildEmailToReplyToMessage(bodyText ?? '')
          : (bodyText ?? ''),
      },
    ];
  }

  private loadDraftAuthorInstruction(): Observable<string | null> {
    return this.maximizerAi
      .lookupPrompts({
        project: OUTLOOK_PROMPT_PROJECT,
        filter: { name: [EMAIL_DRAFT_SYSTEM_PROMPT_NAME] },
      })
      .pipe(
        map((lookupResults) => lookupResults[0]?.prompt ?? null),
        catchError(() => of(null)),
      );
  }
}
